import * as React from 'react';

import { Res, TicketSummaryDTO, FlowAction } from 'core/api';
import { SessionProvider, Events } from 'core/infrastructure';
import { Content, Header, Footer, PosScreen, LargeDeviceOnly, PhoneOnly, Filler, BackActionButton, ActionButton } from 'pos/ui';
import * as Images from 'pos/Images';
import { OrderingFacade } from 'pos/facades';
import { Command } from 'pos/common';
import { BarcodeEncoder } from 'pos/services';
import { Message, Modal } from 'core/ui-next/modal';
import { BarcodeReader } from 'core/ui-next';
import PhoneSelectTicketScreen from './SelectTicketScreen.Phone';
import TabletSelectTicketScreen from './SelectTicketScreen.Tablet';

export type SelectNewTicket = { select: 'new' };
export type SelectExistingTicket = { select: 'existing', ticket: TicketSummaryDTO };
export type TicketSelection = SelectNewTicket | SelectExistingTicket;


type Props = {
	initialTickets: TicketSummaryDTO[];
	title: (tickets: TicketSummaryDTO[]) => string,
	icon: () => string | SvgIcon,
	showCreateNewCommand: boolean,
	showTicketCommands: boolean
} & Modal.Actions<TicketSelection>;

let initiallyShowAllTickets = true;

export function SelectTicketScreen(props: Props): React.ReactNode {

	const { initialTickets, title, icon, showCreateNewCommand, showTicketCommands, cancel, close } = props;


	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	const onGetFilteredTickets = React.useCallback((tickets: TicketSummaryDTO[], showAllTickets: boolean): TicketSummaryDTO[] => {
		const session = SessionProvider.getCurrent();
		return showAllTickets ? tickets : tickets.filter(t => t.OpenerUser === session.UserName);
	}, []);

	const onCreateNewTicket = React.useCallback(
		async () => close({ select: 'new' }
		), [close]);

	const onTicketSelected = React.useCallback(
		(ticket: TicketSummaryDTO) => close({ select: 'existing', ticket }
		), [close]);

	const onShowAllTicketsChanged = React.useCallback((showAllTickets: boolean) => {
		initiallyShowAllTickets = showAllTickets;

		setShowAllTickets(showAllTickets);
		setFilteredTickets(
			onGetFilteredTickets(initialTickets, showAllTickets)
		);
	}, [initialTickets, onGetFilteredTickets]);

	const onBarcodeRead = React.useCallback(async (code: string) => {

		const idResult = await BarcodeEncoder.tryDecodeId(BarcodeEncoder.EntityType.Ticket, code);
		if (!idResult.ok) {
			await Message.show(BarcodeEncoder.isQRCode(code)
				? Res.Msg.MSG_QR_IS_NOT_A_VALID_OPEN_TICKET_QR(code)
				: Res.Msg.MSG_BARCODE_IS_NOT_A_VALID_OPEN_TICKET_BARCODE(code));
			return;
		}

		const ticket = initialTickets.find(x => x.Id === idResult.value) || null;
		if (ticket === null) {
			await Message.show(Res.Msg.MSG_TICKET_NOT_FOUND());
			return;
		}

		onTicketSelected(ticket);
	}, [initialTickets, onTicketSelected]);



	const [filteredTickets, setFilteredTickets] = React.useState<TicketSummaryDTO[]>(
		onGetFilteredTickets(initialTickets, initiallyShowAllTickets)
	);
	const [showAllTickets, setShowAllTickets] = React.useState<boolean>(initiallyShowAllTickets);

	const filteredTicketsRef = React.useRef<TicketSummaryDTO[]>(filteredTickets);
	const showAllTicketsRef = React.useRef<boolean>(showAllTickets);

	React.useEffect(() => {
		filteredTicketsRef.current = filteredTickets;
	}, [filteredTickets]);

	React.useEffect(() => {
		showAllTicketsRef.current = showAllTickets;
	}, [showAllTickets]);

	const onPrintTickets = React.useCallback(async () => {
		await Command.execute('printMultipleTickets', filteredTickets.map(t => t.Id), 'APPLY_TICKET_PRINTED_FLOW');
	}, [filteredTickets]);


	React.useEffect(() => {
		const disposeTicketPrintedEvent = Events.on('pos:sale:ticket-printed', async (flowMode) => {
			if (flowMode === 'IGNORE_TICKET_PRINTED_FLOW')
				return;

			if (SessionProvider.getCurrent().AfterPrintTicketAction !== FlowAction.DoNothing)
				cancel();
		});

		const disposeTicketPreparedEvent = Events.on('pos:sale:ticket-prepared', async () => {
			if (SessionProvider.getCurrent().AfterPrepareTicketAction === FlowAction.DoNothing) {
				const currentTickets = filteredTicketsRef.current;
				const newTickets = await OrderingFacade.getExistingTicketsIn(currentTickets[0].SaleCenterId, currentTickets[0].SaleLocationName);
				setFilteredTickets(onGetFilteredTickets(newTickets, showAllTicketsRef.current));
			}
			else {
				cancel();
			}
		});

		const disposeReader = BarcodeReader.start(onBarcodeRead);

		return () => {
			disposeTicketPrintedEvent();
			disposeTicketPreparedEvent();
			disposeReader();
		};
	}, [onBarcodeRead, onGetFilteredTickets, cancel]);


	return <PosScreen size={{ width: 1000, height: 720 }} screenId={'SELECT_TICKET'} useKeyPad={false}>
		<Header title={title(initialTickets)} image={icon()} onClose={cancel} />
		<Content>
			<LargeDeviceOnly>
				<TabletSelectTicketScreen
					filteredTickets={filteredTickets}
					showAllTickets={showAllTickets}
					onShowAllTicketsChanged={onShowAllTicketsChanged}
					onTicketSelected={onTicketSelected}
				/>
			</LargeDeviceOnly>
			<PhoneOnly>
				<PhoneSelectTicketScreen
					filteredTickets={filteredTickets}
					showAllTickets={showAllTickets}
					onShowAllTicketsChanged={onShowAllTicketsChanged}
					onTicketSelected={onTicketSelected}
				/>
			</PhoneOnly>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
			</PhoneOnly>
			<LargeDeviceOnly>
				<Filler />
			</LargeDeviceOnly>
			{showCreateNewCommand && <ActionButton title={Res.Web.GET_TICKET_IN_LOCATION_NEW_TICKET_BTN()} image={Images.NewTicket} action={onCreateNewTicket} />}
			{showTicketCommands && <ActionButton title={Res.Web.PREPARE_TICKET_BTN()} image={Images.PrepareTicket} action={async () => await Command.execute('prepareMultipleTickets', filteredTickets.map(x => x.Id))} />}
			{showTicketCommands && <ActionButton title={Res.Web.PREPARE_TICKET_AGAIN_BTN()} image={Images.PrepareTicketAgain} action={async () => Command.execute('prepareMultipleTicketsAgain', filteredTickets.map(x => x.Id))} />}
			{showTicketCommands && <ActionButton title={Res.Web.REQUEST_COURSES_BTN()} image={Images.RequestCourses} action={async () => await Command.execute('requestCourses', filteredTickets.map(x => x.Id))} />}
			{showTicketCommands && <ActionButton title={Res.Web.GET_COURSES_TO_PREPARE_BTN()} image={Images.GetCoursesToPrepare} action={async () => Command.execute('showCourses', filteredTickets.map(x => x.Id))} />}
			{showTicketCommands && <ActionButton title={Res.Web.SELECT_TICKET_PRINT_ALL_INVOICES_BTN()} image={Images.PrintTicket} action={onPrintTickets} />}
		</Footer>
	</PosScreen>;
}